// public/v3/src/components/bhi-retag-modal.jsx
//
// Review queue for untagged behavioral time (opened from Time Logs). Each row
// is one untagged activity entry with behavioral evidence in its own text, on
// a BHI-enrolled patient, in a recent month no push has touched. The operator
// retags entries ONE at a time to the patient's BHI code — there is no bulk
// action on purpose: an unreviewed retag manufactures BHI minutes.
//
// Props:
//   candidates — [{ pid, patientName, entry, month, evidence, toProgram }]
//   onRetag(pid, entryId) — async; resolves true on success (row removed)
//   onClose

import React, { useEffect, useState } from 'react';

function entryMinutes(e) {
  return Number(e?.mins) || Number(e?.duration) || 0;
}

// Entry text with the evidence term emphasized, so the operator sees WHY the
// row is here before affirming it.
function EvidenceText({ entry, evidence }) {
  const text = String(entry.desc || entry.notes || entry.label || entry.type || '');
  const i = text.toLowerCase().indexOf(String(evidence).toLowerCase());
  if (i === -1) return <span>{text}</span>;
  return (
    <span>
      {text.slice(0, i)}
      <mark style={{ background: 'var(--accent-soft, #d9f0e5)', padding: '0 2px', borderRadius: 3 }}>
        {text.slice(i, i + evidence.length)}
      </mark>
      {text.slice(i + evidence.length)}
    </span>
  );
}

export function BhiRetagModal({ candidates, onRetag, onClose }) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  async function retag(c) {
    setBusyId(c.entry.id);
    setError(null);
    try {
      const ok = await onRetag(c.pid, c.entry.id);
      if (!ok) setError('Entry could not be retagged (it may have changed elsewhere). Refresh and retry.');
    } catch (e) {
      setError(e?.message || 'Retag failed');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label="Review untagged behavioral time"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 760 }}>
        <div className="modal-head">
          <div>
            <h3>Untagged behavioral time</h3>
            <div className="sub">
              Untagged entries count toward CCM by default. Retag an entry to the patient's BHI code
              only when its own text shows behavioral work — each retag is recorded.
            </div>
          </div>
          <button className="btn ghost icon" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body" style={{ paddingTop: 8 }}>
          {error && (
            <div style={{ color: 'var(--danger, #b3372e)', fontSize: 13, marginBottom: 10 }}>{error}</div>
          )}
          {candidates.length === 0 ? (
            <div style={{
              padding: '32px 20px', textAlign: 'center', color: 'var(--ink-3)',
              fontSize: 13, background: 'var(--surface-2)', borderRadius: 'var(--r)',
            }}>
              Nothing to review — no untagged entries with behavioral evidence in recent unbilled months.
            </div>
          ) : candidates.map((c) => (
            <div
              key={c.pid + ':' + c.entry.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 4px', borderBottom: '1px solid var(--line, #e5e7e6)',
              }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>
                  {c.patientName}
                  <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>
                    {' · '}{c.entry.date}{' · '}{entryMinutes(c.entry)} min
                  </span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--ink-2)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <EvidenceText entry={c.entry} evidence={c.evidence} />
                </div>
              </div>
              <button
                className="btn accent"
                disabled={busyId === c.entry.id}
                onClick={() => retag(c)}
                title={`Tag this entry as ${c.toProgram} so it counts toward BHI, not CCM`}>
                {busyId === c.entry.id ? 'Retagging…' : `Retag → ${c.toProgram}`}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
